import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Button,
  Chip,
  Switch,
  SegmentedButtons,
  IconButton,
  Badge,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import { PersonalizedSmartDashboard } from '../components/ai';
import {
  setCurrentLayout,
  updateSectionCustomization,
  toggleSectionVisibility,
  toggleSectionCollapsed,
  updateContentPrioritization,
  trackInteraction,
  updateUserPreferences,
  selectCurrentLayout,
  selectUserPreferences,
} from '../store/slices/dashboardPersonalizationSlice';
import { getTestingLayoutOptions } from '../config/testingLayouts';
import { DashboardTestingSummary } from '../components/testing/DashboardTestingSummary';
import { RefreshCw, Eye, EyeOff } from 'lucide-react-native';
import { logger } from '../utils/logger';

// type DashboardTestScreenNavigationProp = { navigate: (screen: string, params?: any) => void };

export function DashboardTestScreen() {
  const theme = useTheme();
  const dispatch = useDispatch();

  // State for testing
  const [testMode, setTestMode] = useState<'preview' | 'config' | 'analytics'>(
    'preview'
  );
  const [selectedLayout, setSelectedLayout] = useState('default');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState('beginner');
  const [selectedJourneyStage, setSelectedJourneyStage] =
    useState('onboarding');
  const [showExplanations, setShowExplanations] = useState(true);
  const [showConfidenceIndicators, setShowConfidenceIndicators] =
    useState(true);
  const [maxItems, setMaxItems] = useState(3);
  const [displayStyle, setDisplayStyle] = useState('card');
  const [sortBy, setSortBy] = useState('ai_score');
  const [algorithm, setAlgorithm] = useState('hybrid');
  const [testScenario, setTestScenario] = useState('default');
  // const [autoRefresh, setAutoRefresh] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [interactionCount, setInteractionCount] = useState(0);

  // Redux selectors
  const currentLayout = useSelector(selectCurrentLayout);
  // const availableLayouts = useSelector(selectAvailableLayouts);
  const userPreferences = useSelector(selectUserPreferences);

  // Test user ID
  const testUserId = 'test-user-123';

  // Layout options - combine default and testing layouts
  const defaultLayoutOptions = [
    { value: 'default', label: 'Default' },
    { value: 'beginner_focused', label: 'Beginner' },
    { value: 'advanced_compact', label: 'Advanced' },
  ];

  const testingLayoutOptions = getTestingLayoutOptions();

  // const layoutOptions = [...defaultLayoutOptions, ...testingLayoutOptions];

  // Skill level options
  const skillLevelOptions = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' },
  ];

  // Journey stage options
  const journeyStageOptions = [
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'learning', label: 'Learning' },
    { value: 'practicing', label: 'Practicing' },
    { value: 'mastering', label: 'Mastering' },
  ];

  // Display style options
  const displayStyleOptions = [
    { value: 'card', label: 'Card' },
    { value: 'list', label: 'List' },
    { value: 'compact', label: 'Compact' },
    { value: 'detailed', label: 'Detailed' },
  ];

  // Sort options
  const sortOptions = [
    { value: 'priority', label: 'Priority' },
    { value: 'recency', label: 'Recency' },
    { value: 'engagement', label: 'Engagement' },
    { value: 'ai_score', label: 'AI Score' },
  ];

  // Algorithm options
  const algorithmOptions = [
    { value: 'engagement_based', label: 'Engagement' },
    { value: 'ai_score', label: 'AI Score' },
    { value: 'recency', label: 'Recency' },
    { value: 'hybrid', label: 'Hybrid' },
  ];

  // Test scenario options
  const testScenarioOptions = [
    { value: 'default', label: 'Default' },
    { value: 'new_user', label: 'New User' },
    { value: 'returning_user', label: 'Returning' },
    { value: 'power_user', label: 'Power User' },
    { value: 'struggling_user', label: 'Struggling' },
    { value: 'achievement_focused', label: 'Achievement' },
    { value: 'social_learner', label: 'Social' },
    { value: 'quick_learner', label: 'Quick' },
  ];

  // Section types for testing
  const sectionTypes = [
    'greeting',
    'focus',
    'progress',
    'recommendations',
    'quick_actions',
    'achievements',
    'social',
    'tips',
  ];

  // Action types for testing
  const actionTypes = [
    'continue_journey',
    'try_recipe',
    'browse_recipes',
    'browse_videos',
    'browse_social',
    'watch_video',
    'take_quiz',
    'practice_skill',
    'view_progress',
    'view_achievements',
    'view_favorites',
    'search',
    'try_recommendation',
  ];

  // Handle layout change
  const handleLayoutChange = (layoutId: string) => {
    setSelectedLayout(layoutId);
    dispatch(setCurrentLayout(layoutId));

    // Also update user preferences to reflect the change
    if (userPreferences) {
      dispatch(
        updateUserPreferences({
          ...userPreferences,
          layoutId: layoutId,
          lastUpdated: new Date().toISOString(),
        })
      );
    }
  };

  // Handle section customization
  const handleSectionCustomization = () => {
    dispatch(
      updateSectionCustomization({
        sectionId: 'recommendations',
        customization: {
          showExplanations,
          showConfidenceIndicators,
          maxItems,
          sortBy: sortBy as any,
          filterBy: [],
          displayStyle: displayStyle as any,
        },
      })
    );
  };

  // Handle content prioritization
  const handleContentPrioritization = () => {
    dispatch(
      updateContentPrioritization({
        algorithm: algorithm as any,
        weights: {
          aiConfidence: 0.3,
          userEngagement: 0.25,
          recency: 0.15,
          personalRelevance: 0.2,
          socialProof: 0.1,
        },
      })
    );
  };

  // Test interaction tracking
  const testInteraction = useCallback(
    (sectionId: string, actionType: string) => {
      dispatch(
        trackInteraction({
          userId: testUserId,
          sectionId,
          actionType,
          metadata: {
            test: true,
            timestamp: Date.now(),
            scenario: testScenario,
          },
        })
      );
      setInteractionCount(prev => prev + 1);
      if (showDebugInfo) {
        Alert.alert('Interaction Tracked', `${actionType} in ${sectionId}`);
      }
    },
    [dispatch, testUserId, testScenario, showDebugInfo]
  );

  // Test section visibility toggle
  const testSectionToggle = (sectionId: string) => {
    dispatch(toggleSectionVisibility(sectionId));
    Alert.alert('Section Toggled', `${sectionId} visibility changed`);
  };

  // Test section collapse toggle
  const testSectionCollapse = (sectionId: string) => {
    dispatch(toggleSectionCollapsed(sectionId));
    Alert.alert('Section Collapsed', `${sectionId} collapsed state changed`);
  };

  // Navigation handler
  const handleNavigate = useCallback(
    (screen: string, params?: any) => {
      const message = `Navigate to ${screen}${params ? ` with params: ${JSON.stringify(params)}` : ''}`;
      if (showDebugInfo) {
        Alert.alert('Navigation', message);
      } else {
        logger.log('Navigation:', message);
      }
    },
    [showDebugInfo]
  );

  // Apply test scenario
  const applyTestScenario = useCallback(
    (scenario: string) => {
      let layoutId = 'default';
      let skillLevel = 'beginner';
      let journeyStage = 'onboarding';
      let explanations = true;
      let confidence = true;
      let maxItems = 3;
      let displayStyle = 'card';

      switch (scenario) {
        case 'new_user':
          skillLevel = 'beginner';
          journeyStage = 'onboarding';
          layoutId = 'beginner_focused';
          explanations = true;
          confidence = true;
          maxItems = 2;
          displayStyle = 'detailed';
          break;
        case 'returning_user':
          skillLevel = 'intermediate';
          journeyStage = 'learning';
          layoutId = 'default';
          explanations = true;
          confidence = false;
          maxItems = 4;
          displayStyle = 'card';
          break;
        case 'power_user':
          skillLevel = 'expert';
          journeyStage = 'mastering';
          layoutId = 'advanced_compact';
          explanations = false;
          confidence = false;
          maxItems = 6;
          displayStyle = 'compact';
          break;
        case 'struggling_user':
          skillLevel = 'beginner';
          journeyStage = 'learning';
          layoutId = 'beginner_focused';
          explanations = true;
          confidence = true;
          maxItems = 2;
          displayStyle = 'detailed';
          break;
        case 'achievement_focused':
          skillLevel = 'intermediate';
          journeyStage = 'practicing';
          layoutId = 'default';
          explanations = false;
          confidence = true;
          maxItems = 3;
          displayStyle = 'card';
          break;
        case 'social_learner':
          skillLevel = 'intermediate';
          journeyStage = 'learning';
          layoutId = 'default';
          explanations = true;
          confidence = false;
          maxItems = 4;
          displayStyle = 'card';
          break;
        case 'quick_learner':
          skillLevel = 'advanced';
          journeyStage = 'practicing';
          layoutId = 'advanced_compact';
          explanations = false;
          confidence = false;
          maxItems = 5;
          displayStyle = 'compact';
          break;
        default:
          // Default scenario
          skillLevel = 'beginner';
          journeyStage = 'onboarding';
          layoutId = 'default';
          explanations = true;
          confidence = true;
          maxItems = 3;
          displayStyle = 'card';
      }

      // Update local state
      setSelectedSkillLevel(skillLevel);
      setSelectedJourneyStage(journeyStage);
      setSelectedLayout(layoutId);
      setShowExplanations(explanations);
      setShowConfidenceIndicators(confidence);
      setMaxItems(maxItems);
      setDisplayStyle(displayStyle);

      // Update Redux state
      dispatch(setCurrentLayout(layoutId));

      // Update section customization
      dispatch(
        updateSectionCustomization({
          sectionId: 'recommendations',
          customization: {
            showExplanations: explanations,
            showConfidenceIndicators: confidence,
            maxItems,
            sortBy: 'ai_score',
            filterBy: [],
            displayStyle: displayStyle as any,
          },
        })
      );

      // Update user preferences if they exist
      if (userPreferences) {
        dispatch(
          updateUserPreferences({
            ...userPreferences,
            layoutId: layoutId,
            lastUpdated: new Date().toISOString(),
          })
        );
      }
    },
    [dispatch, userPreferences]
  );

  // Handle scenario change
  const handleScenarioChange = useCallback(
    (scenario: string) => {
      setTestScenario(scenario);
      applyTestScenario(scenario);
    },
    [applyTestScenario]
  );

  // Reset all settings
  const resetAllSettings = useCallback(() => {
    setTestScenario('default');
    applyTestScenario('default');
    setInteractionCount(0);
    Alert.alert(
      'Settings Reset',
      'All settings have been reset to default values'
    );
  }, [applyTestScenario]);

  // Render configuration panel
  const renderConfigurationPanel = () => (
    <ScrollView style={styles.configPanel}>
      <View style={styles.configHeader}>
        <Text
          variant='headlineSmall'
          style={[styles.sectionTitle, { color: theme.colors.onBackground }]}
        >
          Dashboard Configuration
        </Text>
        <View style={styles.headerActions}>
          <IconButton
            icon={() => <RefreshCw size={20} color={theme.colors.onSurface} />}
            onPress={resetAllSettings}
            mode='outlined'
            size={20}
          />
          <IconButton
            icon={() =>
              showDebugInfo ? (
                <EyeOff size={20} color={theme.colors.onSurface} />
              ) : (
                <Eye size={20} color={theme.colors.onSurface} />
              )
            }
            onPress={() => setShowDebugInfo(!showDebugInfo)}
            mode='outlined'
            size={20}
          />
          {interactionCount > 0 && (
            <Badge style={styles.interactionBadge}>{interactionCount}</Badge>
          )}
        </View>
      </View>

      {/* Quick Test Scenarios */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            🎯 Quick Test Scenarios
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}
          >
            Pre-configured scenarios for common user types
          </Text>
          <SegmentedButtons
            value={testScenario}
            onValueChange={handleScenarioChange}
            buttons={testScenarioOptions
              .slice(0, 4)
              .map(b => ({ ...b, style: styles.equalSegment }))}
            style={styles.segmentedButtons}
            density='small'
          />
          <View style={styles.chipContainer}>
            {testScenarioOptions.slice(4).map(option => (
              <Chip
                key={option.value}
                selected={testScenario === option.value}
                onPress={() => handleScenarioChange(option.value)}
                style={styles.scenarioChip}
              >
                {option.label}
              </Chip>
            ))}
          </View>
        </Card.Content>
      </Card>

      {/* Layout Selection */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Layout Selection
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}
          >
            Standard Layouts:
          </Text>
          <SegmentedButtons
            value={selectedLayout}
            onValueChange={handleLayoutChange}
            buttons={defaultLayoutOptions.map(b => ({
              ...b,
              style: styles.equalSegment,
            }))}
            style={styles.segmentedButtons}
            density='small'
          />
          <Text
            variant='bodySmall'
            style={{
              color: theme.colors.onSurfaceVariant,
              marginBottom: 8,
              marginTop: 16,
            }}
          >
            Testing Layouts:
          </Text>
          <View style={styles.chipContainer}>
            {testingLayoutOptions.map(option => (
              <Chip
                key={option.value}
                selected={selectedLayout === option.value}
                onPress={() => handleLayoutChange(option.value)}
                style={styles.layoutChip}
              >
                {option.label}
              </Chip>
            ))}
          </View>
        </Card.Content>
      </Card>

      {/* User Profile */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            User Profile
          </Text>
          <View style={styles.profileRowColumn}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, marginBottom: 6 }}
            >
              Skill Level
            </Text>
            <SegmentedButtons
              value={selectedSkillLevel}
              onValueChange={setSelectedSkillLevel}
              buttons={skillLevelOptions}
              style={styles.fullWidthSegmentedButtons}
              density='small'
            />
          </View>
          <View style={styles.profileRowColumn}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, marginBottom: 6 }}
            >
              Journey Stage
            </Text>
            <SegmentedButtons
              value={selectedJourneyStage}
              onValueChange={setSelectedJourneyStage}
              buttons={journeyStageOptions}
              style={styles.fullWidthSegmentedButtons}
              density='small'
            />
          </View>
        </Card.Content>
      </Card>

      {/* Section Customization */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Section Customization (Recommendations)
          </Text>

          <View style={styles.switchRow}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, flex: 1 }}
            >
              Show Explanations
            </Text>
            <Switch
              value={showExplanations}
              onValueChange={setShowExplanations}
            />
          </View>

          <View style={styles.switchRow}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, flex: 1 }}
            >
              Show Confidence Indicators
            </Text>
            <Switch
              value={showConfidenceIndicators}
              onValueChange={setShowConfidenceIndicators}
            />
          </View>

          <View style={styles.profileRow}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, flex: 1 }}
            >
              Max Items: {maxItems}
            </Text>
            <Button
              mode='outlined'
              compact
              onPress={() => setMaxItems(Math.max(1, maxItems - 1))}
            >
              -
            </Button>
            <Button
              mode='outlined'
              compact
              onPress={() => setMaxItems(Math.min(10, maxItems + 1))}
            >
              +
            </Button>
          </View>

          <View style={styles.profileRowColumn}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, marginBottom: 6 }}
            >
              Display Style
            </Text>
            <SegmentedButtons
              value={displayStyle}
              onValueChange={setDisplayStyle}
              buttons={displayStyleOptions.map(b => ({
                ...b,
                style: styles.equalSegment,
              }))}
              style={styles.fullWidthSegmentedButtons}
              density='small'
            />
          </View>

          <View style={styles.profileRowColumn}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, marginBottom: 6 }}
            >
              Sort By
            </Text>
            <SegmentedButtons
              value={sortBy}
              onValueChange={setSortBy}
              buttons={sortOptions.map(b => ({
                ...b,
                style: styles.equalSegment,
              }))}
              style={styles.fullWidthSegmentedButtons}
              density='small'
            />
          </View>

          <Button
            mode='contained'
            onPress={handleSectionCustomization}
            style={styles.applyButton}
          >
            Apply Section Settings
          </Button>
        </Card.Content>
      </Card>

      {/* Content Prioritization */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Content Prioritization
          </Text>

          <View style={styles.profileRowColumn}>
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface, marginBottom: 6 }}
            >
              Algorithm
            </Text>
            <SegmentedButtons
              value={algorithm}
              onValueChange={setAlgorithm}
              buttons={algorithmOptions.map(b => ({
                ...b,
                style: styles.equalSegment,
              }))}
              style={styles.fullWidthSegmentedButtons}
              density='small'
            />
          </View>

          <Button
            mode='contained'
            onPress={handleContentPrioritization}
            style={styles.applyButton}
          >
            Apply Prioritization
          </Button>
        </Card.Content>
      </Card>

      {/* Section Testing */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Section Testing
          </Text>

          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 8 }}
          >
            Toggle Section Visibility:
          </Text>
          <View style={styles.chipContainer}>
            {sectionTypes.map(sectionId => (
              <Chip
                key={sectionId}
                onPress={() => testSectionToggle(sectionId)}
                style={styles.chip}
              >
                {sectionId}
              </Chip>
            ))}
          </View>

          <Text
            variant='bodyMedium'
            style={{
              color: theme.colors.onSurface,
              marginBottom: 8,
              marginTop: 16,
            }}
          >
            Toggle Section Collapse:
          </Text>
          <View style={styles.chipContainer}>
            {sectionTypes.map(sectionId => (
              <Chip
                key={`collapse-${sectionId}`}
                onPress={() => testSectionCollapse(sectionId)}
                style={styles.chip}
              >
                Collapse {sectionId}
              </Chip>
            ))}
          </View>
        </Card.Content>
      </Card>

      {/* Action Testing */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Action Testing
          </Text>

          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 8 }}
          >
            Test Interactions:
          </Text>
          <View style={styles.chipContainer}>
            {actionTypes.map(actionType => (
              <Chip
                key={actionType}
                onPress={() => testInteraction('quick_actions', actionType)}
                style={styles.chip}
              >
                {actionType}
              </Chip>
            ))}
          </View>
        </Card.Content>
      </Card>

      {/* Current State Display */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Current State
          </Text>

          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 4 }}
          >
            Layout: {currentLayout?.name || 'None'}
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 4 }}
          >
            Skill Level: {selectedSkillLevel}
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 4 }}
          >
            Journey Stage: {selectedJourneyStage}
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 4 }}
          >
            User Preferences: {userPreferences ? 'Loaded' : 'Not loaded'}
          </Text>
        </Card.Content>
      </Card>
    </ScrollView>
  );

  // Render dashboard preview
  const renderDashboardPreview = () => (
    <View style={styles.previewContainer}>
      {showDebugInfo && (
        <View
          style={[
            styles.debugOverlay,
            { backgroundColor: theme.colors.surfaceVariant },
          ]}
        >
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {selectedSkillLevel} • {selectedJourneyStage} • {selectedLayout} •{' '}
            {interactionCount} interactions
          </Text>
        </View>
      )}
      <ScrollView showsVerticalScrollIndicator={false}>
        <DashboardTestingSummary
          layout={selectedLayout}
          skillLevel={selectedSkillLevel}
          journeyStage={selectedJourneyStage}
          interactionCount={interactionCount}
          testScenario={testScenario}
          showExplanations={showExplanations}
          showConfidenceIndicators={showConfidenceIndicators}
          maxItems={maxItems}
          displayStyle={displayStyle}
        />
        <PersonalizedSmartDashboard
          userId={testUserId}
          onNavigate={handleNavigate}
          skillLevel={selectedSkillLevel}
          journeyStage={selectedJourneyStage}
        />
      </ScrollView>
    </View>
  );

  // Render analytics panel
  const renderAnalyticsPanel = () => (
    <ScrollView style={styles.configPanel}>
      <Text
        variant='headlineSmall'
        style={[styles.sectionTitle, { color: theme.colors.onBackground }]}
      >
        📊 Testing Analytics
      </Text>

      {/* Interaction Metrics */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Interaction Metrics
          </Text>
          <View style={styles.metricsGrid}>
            <View style={styles.metricItem}>
              <Text
                variant='headlineMedium'
                style={{ color: theme.colors.primary }}
              >
                {interactionCount}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Total Interactions
              </Text>
            </View>
            <View style={styles.metricItem}>
              <Text
                variant='headlineMedium'
                style={{ color: theme.colors.secondary }}
              >
                {testScenario}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Current Scenario
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Current Configuration */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Current Configuration
          </Text>
          <View style={styles.configSummary}>
            <View style={styles.configRow}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurface }}
              >
                Layout:
              </Text>
              <Chip compact>{selectedLayout}</Chip>
            </View>
            <View style={styles.configRow}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurface }}
              >
                Skill Level:
              </Text>
              <Chip compact>{selectedSkillLevel}</Chip>
            </View>
            <View style={styles.configRow}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurface }}
              >
                Journey Stage:
              </Text>
              <Chip compact>{selectedJourneyStage}</Chip>
            </View>
            <View style={styles.configRow}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurface }}
              >
                Max Items:
              </Text>
              <Chip compact>{maxItems}</Chip>
            </View>
            <View style={styles.configRow}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurface }}
              >
                Display Style:
              </Text>
              <Chip compact>{displayStyle}</Chip>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Performance Metrics */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Performance Insights
          </Text>
          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}
          >
            • Dashboard loads with {selectedLayout} layout
          </Text>
          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}
          >
            • Showing {maxItems} recommendations per section
          </Text>
          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}
          >
            • Explanations: {showExplanations ? 'Enabled' : 'Disabled'}
          </Text>
          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            • Confidence indicators:{' '}
            {showConfidenceIndicators ? 'Enabled' : 'Disabled'}
          </Text>
        </Card.Content>
      </Card>

      {/* Test Actions */}
      <Card
        style={[styles.configCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={{ color: theme.colors.onSurface, marginBottom: 12 }}
          >
            Quick Actions
          </Text>
          <View style={styles.actionButtons}>
            <Button
              mode='outlined'
              onPress={() => setInteractionCount(0)}
              style={styles.actionButton}
            >
              Reset Metrics
            </Button>
            <Button
              mode='outlined'
              onPress={() => handleScenarioChange('new_user')}
              style={styles.actionButton}
            >
              Test New User
            </Button>
            <Button
              mode='outlined'
              onPress={() => handleScenarioChange('power_user')}
              style={styles.actionButton}
            >
              Test Power User
            </Button>
          </View>
        </Card.Content>
      </Card>
    </ScrollView>
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.colors.surface }]}>
        <Text variant='headlineSmall' style={{ color: theme.colors.onSurface }}>
          Dashboard Test Suite
        </Text>
        <SegmentedButtons
          value={testMode}
          onValueChange={setTestMode}
          buttons={[
            { value: 'preview', label: 'Preview', style: styles.equalSegment },
            { value: 'config', label: 'Config', style: styles.equalSegment },
            {
              value: 'analytics',
              label: 'Analytics',
              style: styles.equalSegment,
            },
          ]}
          style={styles.modeButtons}
          density='small'
        />
      </View>

      {/* Content */}
      {testMode === 'preview' && renderDashboardPreview()}
      {testMode === 'config' && renderConfigurationPanel()}
      {testMode === 'analytics' && renderAnalyticsPanel()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'column',
    paddingHorizontal: 16,
    paddingVertical: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  modeButtons: {
    marginTop: 12,
  },
  equalSegment: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewContainer: {
    flex: 1,
    position: 'relative',
  },
  debugOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    paddingHorizontal: 12,
    paddingVertical: 6,
    opacity: 0.9,
  },
  configPanel: {
    flex: 1,
    padding: 16,
  },
  configHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  interactionBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
  },
  sectionTitle: {
    fontWeight: 'bold',
    flex: 1,
  },
  configCard: {
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  segmentedButtons: {
    marginTop: 8,
  },
  smallSegmentedButtons: {
    flex: 1,
  },
  fullWidthSegmentedButtons: {
    width: '100%',
  },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  profileRowColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  applyButton: {
    marginTop: 12,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  chip: {
    marginBottom: 4,
  },
  scenarioChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  layoutChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  metricsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 8,
  },
  metricItem: {
    alignItems: 'center',
    flex: 1,
  },
  configSummary: {
    gap: 8,
  },
  configRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  actionButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  actionButton: {
    flex: 1,
    minWidth: 100,
  },
});
